import {
    IconButton, Input, Menu, MenuButton,
    MenuItem, MenuList, Table, Tbody,
    Td, Th, Thead, Tr,
    VStack, Tooltip, useMediaQuery, HStack, Box, Spinner,
} from '@chakra-ui/react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalFooter, ModalBody, ModalCloseButton, Button,
    useDisclosure, Text,
} from '@chakra-ui/react';
import { BsThreeDots } from 'react-icons/bs';
import { RepeatIcon, DeleteIcon, DownloadIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { useState } from 'react';
import fetchWrapper from '../../lib/fetch-wrapper';
import { url } from '../../lib/constants';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useProfile } from '../../lib/profileContext';
import MultiSelect from '../multiSelect';

interface Props {
    files: any[];
    propertyOptions: { value: string; label: string }[]
    onReplace: (file: any) => void;
    onDelete: (file: any) => void;
    onUpdateProperties: (rowKey: string, updatedProperties: string[]) => void;
}

const FilesTable = ({ files, propertyOptions, onReplace, onDelete, onUpdateProperties }: Props) => {
    const { instance, accounts }: { instance: IPublicClientApplication, accounts: AccountInfo[] } = useMsal();
    const { profile } = useProfile();
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const isMobile = useMediaQuery("(max-width: 768px)")[0];
    const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
    const [propertyEditValues, setPropertyEditValues] = useState<string[]>([]);
    const [originalPropertyValues, setOriginalPropertyValues] = useState<string[]>([]);
    const [pendingRowKey, setPendingRowKey] = useState<string | null>(null);
    const [pendingValues, setPendingValues] = useState<string[]>([]);
    const [isUpdatingProperties, setIsUpdatingProperties] = useState(false);
    const [downloadingRowKey, setDownloadingRowKey] = useState<string | null>(null);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const filteredFiles = files.filter((file) =>
        file.FileName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const sortedFiles = [...filteredFiles].sort((a, b) => {
        if (!sortConfig) return 0; // No sorting applied
        const { key, direction } = sortConfig;

        if (key === 'LastProcessing') {
            const dateA = new Date(a[key]).getTime();
            const dateB = new Date(b[key]).getTime();
            return direction === 'asc' ? dateA - dateB : dateB - dateA;
        }

        const valueA = a[key]?.toString().toLowerCase() || '';
        const valueB = b[key]?.toString().toLowerCase() || '';
        return direction === 'asc'
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
    });

    const handleSort = (key: string) => {
        setSortConfig((prevConfig) => {
            if (prevConfig?.key === key) {
                return { key, direction: prevConfig.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const renderSortIndicator = (key: string) => {
        if (sortConfig?.key !== key) return null;
        return sortConfig.direction === 'asc' ? '▲' : '▼';
    };

    const openFileHandler = async (fileUrl: string, rowKey: string) => {
        setDownloadingRowKey(rowKey);
        try {
            const fw = new fetchWrapper();
            const response = await fw.getDocument(
                `${url}/api/document?url=${encodeURIComponent(fileUrl)}`
            );
            if (response.status === 200) {
                const blobUrl = window.URL.createObjectURL(response.data);
                if (isMobile) {
                    const a = document.createElement('a');
                    a.href = blobUrl;
                    a.download = fileUrl.split('/').pop() || 'document.pdf';
                    a.target = '_self';
                    a.rel = 'noopener noreferrer';

                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                } else {
                    window.open(blobUrl, '_blank');
                }
            } else {
                console.error("Failed to load document");
            }
        } catch (error) {
            console.error("Error fetching document", error);
        } finally {
            setDownloadingRowKey(null);
        }
    };

    const handlePropertiesChange = (selectedOptions: any[]) => {
        const selectedValues = selectedOptions.map((option) => option.value);
        const updated = selectedValues.includes('ALL')
            ? ['ALL']
            : selectedValues.filter((v) => v !== 'ALL');

        setPropertyEditValues(updated);
    };

    return (
        <VStack spacing="4" align="stretch">
            <Input
                placeholder="Search by name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outline"
                size="md"
            />

            <Table
                variant="simple"
                mt="4"
                sx={{
                    '& thead th': {
                        fontWeight: 'bold',
                        color: 'black',
                        textTransform: 'none',
                        fontSize: '1rem',
                        cursor: 'pointer',
                    },
                    '& tbody tr:not(:last-of-type)': {
                        borderBottom: '1px solid',
                        borderColor: '#c2c8d1',
                    },
                }}
            >
                <Thead>
                    <Tr>
                        <Th onClick={() => handleSort('FileName')}>
                            Name {renderSortIndicator('FileName')}
                        </Th>
                        <Th onClick={() => handleSort('Topic')}>
                            Topic {renderSortIndicator('Topic')}
                        </Th>
                        <Th onClick={() => handleSort('Property')}>
                            Properties {renderSortIndicator('Property')}
                        </Th>
                        <Th onClick={() => handleSort('Language')}>
                            Language {renderSortIndicator('Language')}
                        </Th>
                        <Th onClick={() => handleSort('Status')}>
                            Status {renderSortIndicator('Status')}
                        </Th>
                        <Th onClick={() => handleSort('LastProcessing')}>
                            Last Processing {renderSortIndicator('LastProcessing')}
                        </Th>
                        <Th>Actions</Th>
                        <Th></Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {sortedFiles.map((file) => (
                        <Tr key={file.RowKey}>
                            <Td>{file.FileName}</Td>
                            <Td>{file.Topic}</Td>
                            <Td cursor="pointer">
                                {editingRowKey === file.RowKey ? (
                                    <HStack spacing={2} align="center">
                                        <Box w="250px">
                                            <MultiSelect
                                                options={[
                                                    { value: 'ALL', label: 'ALL' },
                                                    ...propertyOptions
                                                ]}
                                                value={propertyEditValues.map((prop) => ({ value: prop, label: prop }))}
                                                onChange={handlePropertiesChange}
                                                placeholder="Select Properties"
                                            />
                                        </Box>
                                        <IconButton
                                            aria-label="Save"
                                            icon={<CheckIcon />}
                                            colorScheme="blue"
                                            size="sm"
                                            onClick={() => {
                                                setPendingValues(propertyEditValues);
                                                setPendingRowKey(file.RowKey);
                                                onOpen(); // open confirmation modal
                                            }}
                                        />
                                        <IconButton
                                            aria-label="Cancel"
                                            icon={<CloseIcon />}
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => {
                                                setEditingRowKey(null);
                                                setPropertyEditValues(originalPropertyValues);
                                            }}
                                        />
                                    </HStack>
                                ) : (
                                    <Text onClick={() => {
                                        setEditingRowKey(file.RowKey);
                                        const currentProps = file.Property?.split(',') || [];
                                        setOriginalPropertyValues(currentProps);
                                        setPropertyEditValues(currentProps);
                                    }}>
                                        {file.Property}
                                    </Text>
                                )}
                            </Td>
                            <Td>{file.Language}</Td>
                            <Td>{file.Status}</Td>
                            <Td>{file.LastProcessing ? formatDate(file.LastProcessing) : ''}</Td>
                            <Td>
                                <Menu>
                                    <Tooltip
                                        label={
                                            (file.Status === 'In Progress' || file.Status === 'Deleting')
                                                ? 'No actions are permitted when the status is either "In Progress" or "Deleting"'
                                                : ''
                                        }
                                        hasArrow
                                        isDisabled={file.Status !== 'In Progress' && file.Status !== 'Deleting'}
                                        bg="red.600"
                                        color="white"
                                    >
                                        <MenuButton
                                            as={IconButton}
                                            aria-label="Options"
                                            icon={<BsThreeDots />}
                                            variant="ghost"
                                        />
                                    </Tooltip>
                                    {
                                        (file.Status !== 'In Progress' && file.Status !== 'Deleting') && (
                                            <MenuList>
                                                <MenuItem icon={<RepeatIcon />} onClick={() => onReplace(file)}>
                                                    Replace
                                                </MenuItem>
                                                <MenuItem icon={<DeleteIcon />} onClick={() => onDelete(file)}>
                                                    Delete
                                                </MenuItem>
                                            </MenuList>
                                        )
                                    }
                                </Menu>
                            </Td>
                            <Td>
                                <IconButton
                                    aria-label="Download"
                                    icon={
                                        downloadingRowKey === file.RowKey
                                            ? <Spinner size="sm" />
                                            : <DownloadIcon />
                                    }
                                    isDisabled={downloadingRowKey === file.RowKey}
                                    onClick={() => openFileHandler(file.FileUrl, file.RowKey)}
                                    variant="ghost"
                                    size="sm"
                                />
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>
            <Modal isOpen={isOpen} onClose={() => {
                onClose();
                setEditingRowKey(null);
                setPropertyEditValues(originalPropertyValues); // revert changes
            }}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Confirm Property Change</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text>Are you sure you want to change the properties to:</Text>
                        <Text fontWeight="bold" mt={2}>
                            {pendingValues.join(', ') || 'None'}
                        </Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={() => {
                            onClose();
                            setEditingRowKey(null);
                            setPropertyEditValues(originalPropertyValues); // revert
                        }}>
                            Cancel
                        </Button>
                        <Button colorScheme="blue" onClick={
                                async () => {
                                    setIsUpdatingProperties(true);
                                    try {
                                        const fw = new fetchWrapper();
                                        await fw.updateFileProperties(pendingRowKey as string, pendingValues);
                                        onUpdateProperties(pendingRowKey as string, pendingValues);
                                    } catch (err) {
                                        console.error("Failed to patch properties", err);
                                    } finally {
                                        setIsUpdatingProperties(false);
                                        onClose();
                                        setEditingRowKey(null);
                                    }
                                }
                            }
                            isLoading={isUpdatingProperties}
                            loadingText="Saving"
                            isDisabled={isUpdatingProperties}
                        >
                            Confirm
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </VStack>
    );
};

const formatDate = (dateString: string) => {
    try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch {
        return 'Invalid date';
    }
};

export default FilesTable;
